import { SignInButton, useAuth } from '@clerk/react';
import { Link } from 'react-router-dom';
import SignUpGateButton from '../components/SignUpGateButton';
import {
  ArrowRight,
  Bell,
  Briefcase,
  Building2,
  CalendarCheck,
  Check,
  Dumbbell,
  HeartPulse,
  MapPin,
  Scissors,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';

export default function Home() {
  const { isLoaded, isSignedIn, orgId } = useAuth();
  const signedInNoOrg = isSignedIn && !orgId;

  return (
    <div className="home">
      {/* Hero ------------------------------------------------- */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-copy">
              <span className="eyebrow">For service businesses</span>
              <h1>
                Less back-and-forth.<br />
                More <em>booked</em> time.
              </h1>
              <p className="hero-sub">
                Invitey is the simplest way to let customers book themselves in.
                Set up your services, share a link, and stop chasing
                appointments over text and DM.
              </p>
              <div className="hero-cta">
                {!isLoaded ? (
                  <span className="muted">Loading…</span>
                ) : !isSignedIn ? (
                  <>
                    <SignUpGateButton className="primary lg">
                      Get started free
                      <ArrowRight size={18} />
                    </SignUpGateButton>
                    <SignInButton mode="modal">
                      <button type="button" className="ghost lg">Sign in</button>
                    </SignInButton>
                  </>
                ) : signedInNoOrg ? (
                  <Link to="/welcome" className="btn primary lg">
                    Finish setup
                    <ArrowRight size={18} />
                  </Link>
                ) : (
                  <Link to="/admin" className="btn primary lg">
                    Open dashboard
                    <ArrowRight size={18} />
                  </Link>
                )}
              </div>
              <p className="hero-trust">
                <Check size={16} color="var(--color-accent)" />
                Free to get started. No credit card required.
              </p>
            </div>

            {/* Mock booking widget — pure CSS, looks like a real booking flow */}
            <div className="hero-mock" aria-hidden="true">
              <div className="hero-mock-head">
                <div className="hero-mock-avatar">S</div>
                <div>
                  <div className="title">Book with Sarah</div>
                  <div className="sub">Northpoint Advisors · 30 min consultation</div>
                </div>
              </div>
              <div className="hero-mock-row">
                <div className="hero-mock-label">Friday, May 16</div>
                <div className="hero-mock-slots">
                  <div className="hero-mock-slot">9:00 AM</div>
                  <div className="hero-mock-slot">9:30 AM</div>
                  <div className="hero-mock-slot active">10:00 AM</div>
                  <div className="hero-mock-slot">11:00 AM</div>
                  <div className="hero-mock-slot">1:30 PM</div>
                  <div className="hero-mock-slot">2:30 PM</div>
                </div>
              </div>
              <div className="hero-mock-row">
                <span className="hero-mock-chip">
                  <MapPin size={13} />
                  Downtown branch
                </span>
              </div>
              <button type="button" className="hero-mock-confirm">
                Confirm 10:00 AM with Sarah
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Industries strip ------------------------------------ */}
      <section className="industries">
        <div className="container industries-inner">
          <span className="industries-label">Made for</span>
          <span className="industry"><ShieldCheck size={18} /> Insurance & financial</span>
          <span className="industry"><Briefcase size={18} /> Agencies & studios</span>
          <span className="industry"><Building2 size={18} /> Real estate</span>
          <span className="industry"><HeartPulse size={18} /> Health & wellness</span>
          <span className="industry"><Scissors size={18} /> Salons & barbers</span>
          <span className="industry"><Dumbbell size={18} /> Trainers & coaches</span>
          <span className="industry"><Sparkles size={18} /> Spas & beauty</span>
        </div>
      </section>

      {/* Features -------------------------------------------- */}
      <section className="section">
        <div className="container">
          <div className="section-heading">
            <span className="eyebrow">Why Invitey</span>
            <h2>Everything you need. Nothing you don&rsquo;t.</h2>
            <p>
              Built for owner-operators and small teams who&rsquo;d rather be
              with customers than wrangling a calendar.
            </p>
          </div>

          <div className="feature-grid">
            <div className="feature-card">
              <div className="feature-icon"><CalendarCheck size={22} /></div>
              <h3>Customers book themselves</h3>
              <p>
                Share one link. They pick a time. The booking lands straight in
                your calendar &mdash; no calls, no DMs, no double-bookings.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon"><Bell size={22} /></div>
              <h3>Reminders that show up for you</h3>
              <p>
                Confirmation emails, 24-hour reminders, and reschedule notices
                go out automatically &mdash; so fewer no-shows on your books.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon"><Users size={22} /></div>
              <h3>Works for your whole team</h3>
              <p>
                Each staff member has their own page, their own hours, and
                their own connected calendar. Customers pick who they want.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon"><MapPin size={22} /></div>
              <h3>Many locations, one link</h3>
              <p>
                Run more than one shop? Customers pick the location closest to
                them on a map, then book the service they need.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works ---------------------------------------- */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-heading">
            <span className="eyebrow">How it works</span>
            <h2>Up and running in 10 minutes.</h2>
            <p>Three short steps. No setup call required.</p>
          </div>

          <div className="steps">
            <div className="step">
              <h3>Add your services and team</h3>
              <p>
                List what you offer, how long it takes, and who at your business
                can deliver it. Edit anytime.
              </p>
            </div>
            <div className="step">
              <h3>Connect your calendar</h3>
              <p>
                Each person on your team links their Google or Outlook calendar,
                so Invitey only ever shows times they&rsquo;re actually free.
              </p>
            </div>
            <div className="step">
              <h3>Share your booking link</h3>
              <p>
                Drop it in your bio, on your website, in an email. Customers
                book themselves in &mdash; you just show up.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA ------------------------------------------- */}
      <section className="section">
        <div className="container">
          <div className="cta-card">
            <h2>Take your first booking today.</h2>
            <p>
              Set up your business in minutes. Free to start.
            </p>
            {!isLoaded ? (
              <span className="muted">Loading…</span>
            ) : !isSignedIn ? (
              <>
                <SignUpGateButton className="primary lg">
                  Get started free
                  <ArrowRight size={18} />
                </SignUpGateButton>
                <p className="small" style={{ marginTop: '1.5rem', color: 'var(--color-text-on-dark-muted)' }}>
                  By signing up you agree to our{' '}
                  <Link to="/terms" style={{ color: 'var(--color-text-on-dark)' }}>Terms</Link>
                  {' '}and{' '}
                  <Link to="/privacy" style={{ color: 'var(--color-text-on-dark)' }}>Privacy Policy</Link>.
                </p>
              </>
            ) : signedInNoOrg ? (
              <Link to="/welcome" className="btn primary lg">
                Finish setup
                <ArrowRight size={18} />
              </Link>
            ) : (
              <Link to="/admin" className="btn primary lg">
                Open dashboard
                <ArrowRight size={18} />
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
